import {Injectable} from '@angular/core';
import type {ContactEvent, EventType} from '../types/contacts.types';

export interface EventMetadata {
  type: EventType;
  eventDate?: string | null;
}

const SEED_BY_NAME: Record<string, EventMetadata> = {
  یلدا: {type: 'shared', eventDate: '1403/09/30'},
  نوروز: {type: 'shared', eventDate: '1403/01/01'},
  'رویداد اول': {type: 'shared', eventDate: '1403/06/15'},
  'تولدهای اردیبهشت': {type: 'individual', eventDate: null},
  'رویداد تستی': {type: 'individual', eventDate: null},
};

const STORAGE_KEY = 'contacts-event-metadata';

interface PersistedEventMetadata {
  byId: Record<string, EventMetadata>;
  byName: Record<string, string>;
}

@Injectable()
export class EventMetadataMockStore {
  private readonly store = new Map<string, EventMetadata>();
  private readonly nameToId = new Map<string, string>();

  constructor() {
    this.loadFromStorage();
  }

  saveMetadata(eventId: string, metadata: EventMetadata, eventName?: string): void {
    this.store.set(eventId, metadata);
    if (eventName?.trim()) {
      this.nameToId.set(eventName.trim(), eventId);
    }
    this.persist();
  }

  getMetadata(eventId: string): EventMetadata | undefined {
    return this.store.get(eventId);
  }

  deleteMetadata(eventId: string): void {
    this.store.delete(eventId);
    for (const [name, id] of this.nameToId.entries()) {
      if (id === eventId) {
        this.nameToId.delete(name);
      }
    }
    this.persist();
  }

  enrichEvent(event: ContactEvent): ContactEvent {
    let stored = this.store.get(event.id);

    if (!stored) {
      const linkedId = this.nameToId.get(event.name);
      if (linkedId) {
        stored = this.store.get(linkedId);
        if (stored) {
          this.store.set(event.id, stored);
          this.nameToId.set(event.name, event.id);
          this.persist();
        }
      }
    }

    const seeded = SEED_BY_NAME[event.name];
    const meta = stored ?? seeded ?? {type: 'shared' as EventType, eventDate: null};

    if (!stored && seeded) {
      this.saveMetadata(event.id, seeded, event.name);
    }

    return {
      ...event,
      type: meta.type,
      eventDate: meta.eventDate ?? null,
    };
  }

  enrichEvents(events: ContactEvent[]): ContactEvent[] {
    return events.map(event => this.enrichEvent(event));
  }

  private loadFromStorage(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw) as PersistedEventMetadata | Record<string, EventMetadata>;
      if ('byId' in parsed && parsed.byId) {
        for (const [id, metadata] of Object.entries(parsed.byId)) {
          this.store.set(id, metadata);
        }
        for (const [name, id] of Object.entries(parsed.byName ?? {})) {
          this.nameToId.set(name, id);
        }
        return;
      }

      for (const [id, metadata] of Object.entries(parsed)) {
        this.store.set(id, metadata);
      }
    } catch {
      this.store.clear();
      this.nameToId.clear();
    }
  }

  private persist(): void {
    try {
      const payload: PersistedEventMetadata = {
        byId: Object.fromEntries(this.store),
        byName: Object.fromEntries(this.nameToId),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // ignore storage failures
    }
  }
}
